import React from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES, DEFAULT_LANGUAGE } from "../i18n";
import { prefixFor, useLang } from "../i18n/LangContext";
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE, OG_LOCALES, organizationJsonLd, websiteJsonLd } from "./site";

/**
 * Per-page <head> metadata + JSON-LD.
 * React 19 hoists <title>, <meta> and <link> rendered anywhere in the tree into <head>,
 * so no helmet library is needed. The prerender step captures the result as static HTML.
 *
 * @param {string}  page       key under `seo.pages` in the locale files (title + description)
 * @param {string}  path       language-agnostic path ("/about", "/stories/<slug>")
 * @param {object}  [override] { title, description, image, type, noindex, languages, breadcrumbs: [{name, path}], jsonLd: [] }
 *                             languages: codes the page exists in (default: all). A single-language page
 *                             gets hreflang for those codes only, and no x-default.
 */
const Seo = ({ page, path, override = {} }) => {
  const { t } = useTranslation();
  const { lang, prefix } = useLang();

  const pageTitle = override.title || t(`seo.pages.${page}.title`);
  const description = override.description || t(`seo.pages.${page}.description`);
  const title = page === "home" ? pageTitle : `${pageTitle} | ${SITE_NAME}`;
  const image = override.image ? (override.image.startsWith("http") ? override.image : `${SITE_URL}${override.image}`) : DEFAULT_OG_IMAGE;
  const url = `${SITE_URL}${prefix}${path === "/" && prefix ? "" : path}`;
  const type = override.type || "website";
  const langs = override.languages ? LANGUAGES.filter((l) => override.languages.includes(l.code)) : LANGUAGES;

  const breadcrumbs = override.breadcrumbs || [];
  const graph = [
    organizationJsonLd(lang, t("seo.orgDescription")),
    websiteJsonLd(lang),
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: title,
      description,
      inLanguage: lang,
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
      primaryImageOfPage: { "@type": "ImageObject", url: image },
    },
    ...(breadcrumbs.length
      ? [{
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            item: `${SITE_URL}${prefix}${b.path === "/" && prefix ? "" : b.path}`,
          })),
        }]
      : []),
    ...(override.jsonLd || []),
  ];

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {override.noindex ? <meta name="robots" content="noindex,follow" /> : <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />}
      <link rel="canonical" href={url} />
      {langs.map(({ code }) => (
        <link key={code} rel="alternate" hrefLang={code} href={`${SITE_URL}${prefixFor(code)}${path === "/" && code !== DEFAULT_LANGUAGE ? "" : path}`} />
      ))}
      {!override.languages && <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${path}`} />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={OG_LOCALES[lang]} />
      {langs.filter((l) => l.code !== lang).map(({ code }) => (
        <meta key={code} property="og:locale:alternate" content={OG_LOCALES[code]} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ "@context": "https://schema.org", "@graph": graph }) }} />
    </>
  );
};

export default Seo;
